// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import type { StoreSpecial as V3StoreSpecial } from '@iheartjane/data-access';
import { ALL_STORE_SPECIALS_OPTS } from '@iheartjane/data-access';
import { useStoreSpecials } from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { Flex, TagFilledIcon, Typography } from '@iheartjane/reefer';
import capitalize from 'lodash-es/capitalize';
import { useMemo } from 'react';

import {
  useGetStoreSpecials,
  useGetStoreSpecialsPage,
} from '@jane/shared-ecomm/data-access';
import { useGetStore } from '@jane/shared/data-access';
import type { Store, StoreSpecial } from '@jane/shared/types';

import type { RootLoaderType } from '../../../data-access';
import { useTypedRouteLoaderData } from '../../../hooks';
import { formatGroupDiscountTitle } from '../../../util';
import { Banner } from './Banner';

const StaticTagIcon = styled(TagFilledIcon)({
  minWidth: 16,
  minHeight: 16,
  alignSelf: 'center',
});

interface SpecialsVariations {
  bulkSpecials: (StoreSpecial | V3StoreSpecial)[];
  bundleSpecials: (StoreSpecial | V3StoreSpecial)[];
  cartTotalSpecials: (StoreSpecial | V3StoreSpecial)[];
  groupSpecials: (StoreSpecial | V3StoreSpecial)[];
  productSpecials: (StoreSpecial | V3StoreSpecial)[];
  spendingThresholdSpecials: (StoreSpecial | V3StoreSpecial)[];
}

const SpecialTypeSection = ({
  type,
  titles,
}: {
  titles: string[];
  type: string;
}) => {
  if (titles.length < 1) {
    return null;
  }
  return (
    <>
      <Typography mr={8}>{type}:</Typography>{' '}
      {titles.map((title) => (
        <Flex key={title} flexDirection="row">
          <StaticTagIcon mr={4} size="sm" color="sunset" />
          <Typography mr={8} color="sunset">
            {title}
          </Typography>
        </Flex>
      ))}
    </>
  );
};

const DealList = ({
  specials,
  store,
}: {
  specials: (StoreSpecial | V3StoreSpecial)[];
  store: Store;
}) => {
  const specialVariations = specials.reduce(
    (acc: SpecialsVariations, special: StoreSpecial | V3StoreSpecial) => {
      switch (special.special_type) {
        case 'bulk_pricing':
          acc.bulkSpecials.push(special);
          break;
        case 'bundle':
          acc.bundleSpecials.push(special);
          break;
        case 'cart_total':
          acc.cartTotalSpecials.push(special);
          break;
        case 'product':
          acc.productSpecials.push(special);
          break;
        case 'qualified_group':
          acc.groupSpecials.push(special);
          break;
        case 'spending_threshold':
          acc.spendingThresholdSpecials.push(special);
          break;
        default:
          break;
      }
      return acc;
    },
    {
      bulkSpecials: [],
      bundleSpecials: [],
      cartTotalSpecials: [],
      groupSpecials: [],
      productSpecials: [],
      spendingThresholdSpecials: [],
    }
  );
  return (
    <>
      <SpecialTypeSection
        titles={specialVariations.productSpecials.map(({ title }) => title)}
        type="Products"
      />
      <SpecialTypeSection
        titles={specialVariations.bundleSpecials.map(({ title }) => title)}
        type={
          capitalize(
            store?.store_compliance_language_settings?.['bundle'] || ''
          ) || 'Bundle'
        }
      />
      <SpecialTypeSection
        titles={specialVariations.bulkSpecials.map(({ title }) => title)}
        type="Bulk"
      />
      <SpecialTypeSection
        titles={specialVariations.cartTotalSpecials.map(({ title }) => title)}
        type="Cart Total"
      />
      <SpecialTypeSection
        titles={specialVariations.groupSpecials.map((special) =>
          formatGroupDiscountTitle(special)
        )}
        type="Group"
      />
      <SpecialTypeSection
        titles={specialVariations.spendingThresholdSpecials.map(
          ({ title }) => title
        )}
        type="Spending Threshold"
      />
    </>
  );
};

export const DealBanner = () => {
  const useV3 = useFlag(FLAGS.c3UseV3StoreSpecials);
  const { store: initialStore, storeId } =
    useTypedRouteLoaderData<RootLoaderType>('root');
  const { data: store } = useGetStore<Store>(storeId, {
    initialData: initialStore,
  });

  // Main "all specials" fetch. v1 hits the non-paginated endpoint; v3 reuses
  // the paginated hook with includeHidden: true to mirror that all-inclusive
  // behavior (the page-level hidden filter below still hides the ones we
  // don't want in the banner).
  const v1MainQuery = useGetStoreSpecials(storeId, { enabled: !useV3 });
  const v3MainQuery = useStoreSpecials({
    ...ALL_STORE_SPECIALS_OPTS,
    enabled: !!useV3,
    storeId,
  });

  // Row fetch — used only to compute which special ids the row is rendering.
  // Default args so the cache key matches MenuProvider / StoreSpecialsRow.
  const v1RowQuery = useGetStoreSpecialsPage(storeId, { enabled: !useV3 });
  const v3RowQuery = useStoreSpecials({
    appMode: 'kiosk',
    enabled: !!useV3,
    storeId,
  });

  const rowSpecialIds = useMemo(() => {
    const ids = useV3
      ? (v3RowQuery.data?.pages ?? []).flatMap((page) =>
          page.specials.map((s) => s.id)
        )
      : (v1RowQuery.data?.pages ?? []).flatMap((page) =>
          page.specials.map((s) => s.id)
        );
    return new Set(ids);
  }, [useV3, v1RowQuery.data, v3RowQuery.data]);

  const { specials_row_configs } = store;

  const relevantSpecials = useMemo<(StoreSpecial | V3StoreSpecial)[]>(() => {
    if (useV3) {
      const v3Specials = (v3MainQuery.data?.pages ?? []).flatMap(
        (page) => page.specials
      );
      // v3 backend pre-filters enabled and promo_code, but `default` app_mode
      // skips the reservation_modes filter — apply it client-side to match v1.
      return v3Specials.filter(
        ({ special_type, id, hidden_from_page, reservation_modes }) => {
          if (
            ((special_type === 'product' || special_type === 'bundle') &&
              (specials_row_configs?.enabled || !rowSpecialIds.has(id))) ||
            hidden_from_page
          ) {
            return false;
          }
          return reservation_modes?.['pickup'] || reservation_modes?.['kiosk'];
        }
      );
    }

    return (v1MainQuery.data ?? []).filter(
      ({
        enabled,
        reservation_modes,
        promo_code,
        special_type,
        id,
        hidden_from_page,
      }) => {
        if (
          ((special_type === 'product' || special_type === 'bundle') &&
            (specials_row_configs?.enabled || !rowSpecialIds.has(id))) ||
          hidden_from_page
        ) {
          return false;
        }
        return (
          enabled &&
          (reservation_modes?.['pickup'] || reservation_modes?.['kiosk']) &&
          !promo_code
        );
      }
    );
  }, [
    useV3,
    v1MainQuery.data,
    v3MainQuery.data,
    rowSpecialIds,
    specials_row_configs?.enabled,
  ]);

  return relevantSpecials.length > 0 ? (
    <Banner>
      <DealList store={store} specials={relevantSpecials} />
    </Banner>
  ) : null;
};
